/**
 * Atoll shared module initialisation.
 *
 * Every Atoll-compliant frontend must call initAtoll() once at startup,
 * before using any Atoll shared module. This provides project-specific
 * configuration (API base URL, error handling) without shared modules
 * needing to import from project-specific files.
 *
 * Usage in your App.tsx or entry point:
 *
 *   import { initAtoll } from '@atoll/shared/atoll/init';
 *
 *   initAtoll({
 *     baseURL: '/',                          // API base URL for this project
 *     errorCallback: myErrorHandler,          // optional: custom error handler
 *   });
 *
 * Shared modules read config via getAtollConfig(). If initAtoll() has not
 * been called, getAtollConfig() throws with a clear error message.
 */

export interface AtollConfig {
  /** Project API base URL. Defaults to '/' (same-origin). */
  baseURL: string;
  /** Atoll service base URL for auth, prefs, event registration. Defaults to baseURL. */
  atollBaseURL: string;
  /** WebSocket URL for shute. Derived from atollBaseURL + 'shute' if not set. */
  shuteURL: string;
  /** Registered service identifier (e.g. "atoll", "apestaart", "kleos"). Used as client_id in auth calls. */
  clientId: string;
  /** Optional error callback for AJAX failures. Called with the error outcome. */
  errorCallback?: (outcome: any) => boolean;
}

const defaultConfig: Partial<AtollConfig> = {
  baseURL: '/',
  clientId: 'atoll',
  errorCallback: (outcome: any) => {
    console.error('Atoll AJAX error:', outcome);
    return false;
  },
};

let _config: AtollConfig | null = null;
let _initialised = false;

/**
 * Initialise the Atoll shared module configuration.
 * Must be called once at startup before any Atoll shared module is used.
 */
export function initAtoll(config: Pick<AtollConfig, 'baseURL' | 'atollBaseURL' | 'shuteURL' | 'clientId'> & Partial<AtollConfig>): void {
  _config = { ...defaultConfig, ...config } as AtollConfig;
  _initialised = true;
}

/**
 * Read the current Atoll configuration.
 * Throws if initAtoll() has not been called.
 *
 * Used internally by Atoll shared modules — not typically called by project code.
 */
export function getAtollConfig(): AtollConfig {
  if (!_initialised || !_config) {
    throw new Error(
      'Atoll shared modules not initialised. Call initAtoll() in your app entry point before using Atoll shared modules. ' +
      'See the Shared Module init.ts wiki page for details.'
    );
  }
  return _config;
}

/**
 * Convenience getter for the project API base URL.
 * Used by project-specific API clients (e.g. apestaart events, interact).
 */
export function getAtollBaseURL(): string {
  return getAtollConfig().baseURL;
}

/**
 * Convenience getter for the Atoll service base URL.
 * Used by Atoll shared modules for auth, prefs, event registration.
 * Falls back to baseURL if atollBaseURL is not set.
 */
export function getAtollServiceURL(): string {
  const config = getAtollConfig();
  return config.atollBaseURL;
}

/**
 * Convenience getter for the shute WebSocket URL.
 * Derives from atollBaseURL if not explicitly set.
 */
export function getShuteURL(): string {
  return getAtollConfig().shuteURL;
}

/**
 * Convenience getter for the client_id.
 * Used by authenticateUser, refreshSession for the client_id field in auth requests.
 */
export function getAtollClientId(): string {
  return getAtollConfig().clientId;
}

/**
 * Convenience getter for the error callback.
 */
export function getAtollErrorCallback(): (outcome: any) => boolean {
  return getAtollConfig().errorCallback || defaultConfig.errorCallback!;
}
